/**
 * DesktopLockScreen
 *
 * Full-screen overlay shown on Electron whenever the app is locked, regardless
 * of which signal triggered the lock:
 *  - Idle timeout (default 10 min — kept in sync with mobile LockScreen subtitle)
 *  - OS screen lock / machine sleep (Electron main → `onLockScreen`)
 *  - User pressing Cmd+L / File → Lock Screen
 *  - JS-side `SET_LOCKED { show: true }` (manual lock from Settings, bg→active
 *    inactivity tracker in RootNavigator, auth bootstrap, etc.)
 *
 * Authentication options (in priority order):
 *  1. Touch ID / Windows Hello (if available on this machine)
 *  2. App PIN — same 4-digit PIN set up in the mobile app, stored as
 *     SHA256("${cognito_id}:${pin}") on `user.pin`. Verified via the same
 *     hashing that `PinSetupModal` writes and `LockScreen` checks, so the
 *     desktop and mobile flows agree on a single source of truth.
 *
 * If the user can't recall their PIN there's a Sign Out escape — without it a
 * forgotten PIN + no Touch ID = permanently bricked desktop install (the
 * progressive lockout makes brute-forcing untenable as well).
 *
 * The window content is already protected by `setContentProtection(true)` in
 * main.ts, so this overlay is defence-in-depth — the underlying screen is
 * invisible to screenshots and screen recording regardless.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Image,
} from 'react-native';
import { useSelector } from 'react-redux';
import * as Crypto from 'expo-crypto';
import { getElectronBridge } from '../utilities/platform';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { useAppDispatch } from '../store';
import { signOut } from '../store/actions/auth';
import { SET_LOCKED } from '../store/constants';
import type { RootState } from '../store';

interface Props {
  onUnlock: () => void;
}

// Mirrors PinSetupModal.tsx and LockScreen.tsx — single source of truth for
// PIN length lives across all three; bumping it requires changing all three
// in lockstep so existing stored hashes remain verifiable.
const PIN_LENGTH = 4;
const MAX_ATTEMPTS = 5;

export default function DesktopLockScreen({ onUnlock }: Props) {
  const bridge = getElectronBridge();
  const dispatch = useAppDispatch();

  const user        = useSelector((s: RootState) => s.authentication.user);
  const cognitoId   = user?.cognito_id;
  const storedHash  = user?.pin;
  const firstName   = (user?.first_name || 'there').trim();
  const displayName = firstName.charAt(0).toUpperCase() + firstName.slice(1);

  const [pin, setPin]                   = useState('');
  const [error, setError]               = useState('');
  const [attempts, setAttempts]         = useState(0);
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioLoading, setBioLoading]     = useState(false);
  const [verifying, setVerifying]       = useState(false);
  const [blocked, setBlocked]           = useState(false);
  const [blockSeconds, setBlockSeconds] = useState(0);
  const inputRef = useRef<TextInput>(null);

  // ── Check biometric availability once on mount ───────────────────────────
  useEffect(() => {
    bridge?.biometricsAvailable?.().then((avail: boolean) => setBioAvailable(!!avail));
  }, [bridge]);

  // ── Always focus the PIN input on mount ──────────────────────────────────
  // We deliberately do NOT auto-trigger Touch ID. Surprising the user with a
  // system biometric prompt the moment they wake the desktop is jarring; if
  // they want it, the explicit "Use Touch ID" button is right there. Focusing
  // the hidden input lets them just start typing — no tap-to-focus required.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // ── Lockout countdown ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!blocked || blockSeconds <= 0) return;
    const t = setInterval(() => {
      setBlockSeconds((s) => {
        if (s <= 1) { clearInterval(t); setBlocked(false); setAttempts(0); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [blocked, blockSeconds]);

  const triggerBiometrics = useCallback(async () => {
    if (!bridge?.promptBiometrics || bioLoading) return;
    setBioLoading(true);
    setError('');
    try {
      const result = await bridge.promptBiometrics('unlock Eleplan');
      if (result.success) {
        onUnlock();
        return;
      }
      // Suppress the red banner when the user cancelled the prompt themselves
      // (cancel button, hit Esc, or chose the password fallback). Only show
      // the error when biometric auth actually failed (e.g. unrecognised
      // fingerprint, lockout). 'not-available' = no hardware path, also silent.
      const msg = (result.error || '').toLowerCase();
      const isCancelled  = msg.includes('cancel') || msg.includes('fallback') || msg.includes('user name');
      const isUnavailable = msg.includes('not-available') || msg.includes('not available');
      if (!isCancelled && !isUnavailable) {
        setError('Biometric authentication failed. Enter your PIN.');
      }
      inputRef.current?.focus();
    } finally {
      setBioLoading(false);
    }
  }, [bridge, bioLoading, onUnlock]);

  const verifyPin = useCallback(async (value: string) => {
    if (!cognitoId || !storedHash) {
      // No PIN configured for this user — shouldn't happen if the lock screen
      // is visible (lock fires only after auth + PIN setup), but fail safe.
      setError('No PIN is configured for this account. Sign out and sign back in.');
      setPin('');
      return;
    }
    setVerifying(true);
    try {
      const hashed = await Crypto.digestStringAsync(
        Crypto.CryptoDigestAlgorithm.SHA256,
        `${cognitoId}:${value}`,
      );
      if (hashed === storedHash) {
        onUnlock();
        return;
      }
      const next = attempts + 1;
      setAttempts(next);
      setPin('');
      if (next >= MAX_ATTEMPTS) {
        // Progressive lockout: 30 s per block of 5 attempts
        const seconds = 30 * Math.ceil(next / MAX_ATTEMPTS);
        setBlocked(true);
        setBlockSeconds(seconds);
        setError(`Too many attempts. Try again in ${seconds}s.`);
      } else {
        const remaining = MAX_ATTEMPTS - next;
        setError(`Incorrect PIN. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`);
        inputRef.current?.focus();
      }
    } catch {
      setError('Verification failed. Try again.');
      setPin('');
    } finally {
      setVerifying(false);
    }
  }, [attempts, cognitoId, storedHash, onUnlock]);

  const handlePinChange = useCallback((text: string) => {
    if (blocked || verifying) return;
    // Digits only, max PIN_LENGTH
    const digits = text.replace(/\D/g, '').slice(0, PIN_LENGTH);
    setPin(digits);
    setError('');
    if (digits.length === PIN_LENGTH) verifyPin(digits);
  }, [blocked, verifying, verifyPin]);

  const handleSignOut = useCallback(async () => {
    // Mirror DesktopLayout.handleUnlock: clear both lock layers before
    // signing out so the post-signOut LoginScreen isn't behind a stale
    // overlay. signOut() already clears user state which would tear the
    // lock screen down on its own, but explicit is cheaper than implicit.
    try { bridge?.unlockScreen?.(); } catch { /* native: no-op */ }
    dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } });
    await dispatch(signOut());
  }, [bridge, dispatch]);

  return (
    // <Modal> (vs a plain absolute-fill View) puts the lock overlay in the
    // top-most layer — above any react-native <Modal> that happened to be
    // open when the lock fired (sheets, dialogs, etc). On react-native-web
    // (Electron) Modals render via `createPortal` to `document.body`, so a
    // sibling View — even with `zIndex: 9999` — still paints below an open
    // modal portal. Mounting our own Modal here joins the same overlay
    // stack and, because it mounts later, sits on top.
    <Modal
      visible
      transparent
      animationType="none"
      statusBarTranslucent
      hardwareAccelerated
      onRequestClose={() => { /* lock is non-dismissible without auth */ }}
    >
      <View style={styles.overlay}>
        <Image
          source={require('../../assets/icon.png')}
          style={styles.logo}
          resizeMode="contain"
        />

        <Text style={styles.appName}>Eleplan</Text>
        <Text style={styles.subtitle}>
          {displayName !== 'There' ? `Welcome back, ${displayName}` : 'Your session is locked'}
        </Text>

        {bioAvailable && (
          <TouchableOpacity
            style={styles.bioButton}
            onPress={triggerBiometrics}
            disabled={bioLoading || blocked}
            accessibilityLabel="Unlock with Touch ID"
          >
            {bioLoading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <>
                <FontAwesomeIcon icon={['fad', 'fingerprint'] as any} size={20} color="#fff" />
                <Text style={styles.bioButtonText}>Use Touch ID</Text>
              </>
            )}
          </TouchableOpacity>
        )}

        {bioAvailable && (
          <View style={styles.dividerRow}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>or enter PIN</Text>
            <View style={styles.dividerLine} />
          </View>
        )}

        <View style={styles.dotsRow}>
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <View
              key={i}
              style={[styles.dot, pin.length > i && styles.dotFilled]}
            />
          ))}
        </View>

        <TextInput
          ref={inputRef}
          style={styles.hiddenInput}
          value={pin}
          onChangeText={handlePinChange}
          // Re-focus on blur — the user shouldn't ever have to click back
          // into the (invisible) input to keep typing. The lock screen has
          // exactly one input target.
          onBlur={() => {
            if (!blocked && !verifying) {
              setTimeout(() => inputRef.current?.focus(), 0);
            }
          }}
          keyboardType="number-pad"
          secureTextEntry
          maxLength={PIN_LENGTH}
          editable={!blocked && !verifying}
          autoFocus
          accessibilityLabel="PIN input"
        />

        <TouchableOpacity
          onPress={() => inputRef.current?.focus()}
          style={styles.tapTarget}
          accessibilityLabel="PIN input — focus to type"
        >
          <Text style={styles.tapHint}>
            {blocked
              ? `Locked for ${blockSeconds}s`
              : verifying
                ? 'Verifying…'
                : 'Type your PIN'}
          </Text>
        </TouchableOpacity>

        {!!error && <Text style={styles.error}>{error}</Text>}

        {/* Recovery escape: forgotten PIN + no biometrics would otherwise
            brick the desktop install. Sign-out drops both lock layers and
            sends them through the normal auth flow on next launch. */}
        <TouchableOpacity
          onPress={handleSignOut}
          style={styles.signOutLink}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          accessibilityLabel="Sign out"
        >
          <Text style={styles.signOutText}>
            Not {displayName}?{' '}
            <Text style={styles.signOutHighlight}>Sign Out</Text>
          </Text>
        </TouchableOpacity>

        <Text style={styles.footer}>
          All data is encrypted at rest using your device keychain.
        </Text>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#0f172a',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    gap: 16,
  },
  logo: {
    width: 72,
    height: 72,
    borderRadius: 16,
    marginBottom: 4,
  },
  appName: {
    fontSize: 28,
    fontWeight: '700',
    color: '#f1f5f9',
    letterSpacing: 0.5,
  },
  subtitle: {
    fontSize: 15,
    color: '#94a3b8',
    marginBottom: 8,
  },
  bioButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#e07150',
    paddingVertical: 12,
    paddingHorizontal: 28,
    borderRadius: 12,
    marginTop: 4,
  },
  bioButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: 280,
    gap: 10,
    marginVertical: 4,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#334155',
  },
  dividerText: {
    color: '#64748b',
    fontSize: 13,
  },
  dotsRow: {
    flexDirection: 'row',
    gap: 14,
    marginTop: 4,
  },
  dot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 2,
    borderColor: '#475569',
    backgroundColor: 'transparent',
  },
  dotFilled: {
    backgroundColor: '#e07150',
    borderColor: '#e07150',
  },
  hiddenInput: {
    position: 'absolute',
    opacity: 0,
    width: 1,
    height: 1,
  },
  tapTarget: {
    marginTop: 8,
    paddingVertical: 6,
    paddingHorizontal: 16,
  },
  tapHint: {
    color: '#64748b',
    fontSize: 13,
  },
  error: {
    color: '#f87171',
    fontSize: 13,
    maxWidth: 280,
    textAlign: 'center',
  },
  signOutLink: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginTop: 4,
  },
  signOutText: {
    color: '#64748b',
    fontSize: 13,
    textAlign: 'center',
  },
  signOutHighlight: {
    color: '#e07150',
    fontWeight: '600',
  },
  footer: {
    position: 'absolute',
    bottom: 28,
    color: '#334155',
    fontSize: 12,
    textAlign: 'center',
  },
});
